import React, { useMemo, useState } from "react";

import { mdiTrashCanOutline } from "@mdi/js";
import Dropzone from "react-dropzone";
import { useTranslation } from "react-i18next";

import SourceMediaPanelContent from "./SourceMediaPanelContent";
import SourceMediaPanelHeader from "./SourceMediaPanelHeader";
import Block from "../../block/Block";
import ConfirmModal from "../../modal/ConfirmModal";
import UploadFromUrlModal from "../UploadFromUrlModal";

import type { UploadMode } from "./types";
import type {
  PivotMedia,
  SourceMedia,
  UploadFromUrlData,
} from "../../../api/EncoderAPI.types";
import type { ModalActions } from "../../modal/Modal";

const PENDING_SOURCE_UUID = "uploading";

interface SourceMediaPanelProps {
  sourceMedias: (SourceMedia | PivotMedia)[];
  isAudioOnly: boolean;
  isEncoding: boolean;
  isSimpleMode?: boolean;
  isUploadBlocked: boolean;
  isUploading: boolean;
  isQueued: boolean;
  isUploadError: boolean;
  uploadProgress: number;
  currentFile: File | null;
  uploadedMimeTypeMap: Record<string, string>;
  isLoading?: boolean;
  onAddFile: (files: File[]) => void;
  onDeleteSourceMedia: (sourceMediaId: string, filename: string) => void;
  onResetUpload: () => void;
  onUploadFromUrl: (data: UploadFromUrlData) => void;
}

export default function SourceMediaPanel({
  sourceMedias,
  isAudioOnly,
  isEncoding,
  isSimpleMode,
  isUploadBlocked,
  isUploading,
  isQueued,
  isUploadError,
  uploadProgress,
  currentFile,
  uploadedMimeTypeMap,
  isLoading,
  onAddFile,
  onDeleteSourceMedia,
  onResetUpload,
  onUploadFromUrl,
}: SourceMediaPanelProps): React.JSX.Element {
  const { t } = useTranslation("editor-content");

  const [isFromUrlModalOpen, setIsFromUrlModalOpen] = useState(false);
  const [sourceToDelete, setSourceToDelete] = useState<{
    uuid: string;
    filename: string;
  } | null>(null);

  const deleteModalActions: ModalActions[] = [
    {
      label: t("removeSourceModal.cancel"),
      onClick: (): void => setSourceToDelete(null),
      buttonVariant: "secondary",
    },
    {
      label: t("removeSourceModal.confirm"),
      onClick: (): void => {
        if (sourceToDelete) {
          onDeleteSourceMedia(sourceToDelete.uuid, sourceToDelete.filename);
        }
        setSourceToDelete(null);
      },
      buttonVariant: "delete",
    },
  ];

  const hasVideoSource = sourceMedias.some((src) =>
    src.info?.streams?.some((s) => s.codec_type === "video"),
  );
  const hasAudioSource = sourceMedias.some((src) =>
    src.info?.streams?.some((s) => s.codec_type === "audio"),
  );

  const uploadMode = ((): UploadMode => {
    if (isAudioOnly && hasAudioSource) return "none";
    if (hasVideoSource) return "videoLocked";
    return "open";
  })();

  const dropzoneAccept: Record<string, string[]> | undefined = {
    audioOnly: { "audio/*": [] },
    videoLocked: { "audio/*": [], "text/vtt": [".vtt"] },
    none: undefined,
    open: undefined,
  }[uploadMode];

  const pendingSource: SourceMedia | null =
    (isUploading || isUploadError) && currentFile
      ? {
          uuid: PENDING_SOURCE_UUID,
          filename: currentFile.name,
          available: false,
          info: null,
          media_uuid: "",
          progression: null,
          status: isUploadError ? "error" : "uploading",
        }
      : null;

  const sortedSourceMediaList = useMemo(
    () =>
      [...sourceMedias].sort((a, b) => {
        const aHasVideo = a.info?.streams?.some((s) => s.codec_type === "video")
          ? 0
          : 1;
        const bHasVideo = b.info?.streams?.some((s) => s.codec_type === "video")
          ? 0
          : 1;
        return aHasVideo - bHasVideo;
      }),
    [sourceMedias],
  );

  const isDisabledDropzone =
    isLoading ||
    isEncoding ||
    isUploading ||
    isUploadBlocked ||
    uploadMode === "none";

  return (
    <React.Fragment>
      <Dropzone
        noClick
        accept={dropzoneAccept}
        disabled={isDisabledDropzone}
        multiple={false}
        onDropAccepted={onAddFile}
      >
        {({
          getRootProps,
          getInputProps,
          isDragAccept,
          isDragReject,
          open,
        }): React.JSX.Element => (
          <div
            {...getRootProps()} // eslint-disable-line react/jsx-props-no-spreading
            className="h-full outline-none"
          >
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <input {...getInputProps()} />
            <Block
              isFullSize
              isScrollableChildren
              classNameHeader="h-[4rem]"
              title={t("sourceMediaPanelTitle")}
              headerComponent={
                <SourceMediaPanelHeader
                  isAudioOnly={isAudioOnly}
                  isEncoding={isEncoding}
                  isLoading={isLoading}
                  isUploadBlocked={isUploadBlocked}
                  isUploading={isUploading}
                  isVideoSourceLocked={hasVideoSource}
                  onOpenFromUrl={(): void => setIsFromUrlModalOpen(true)}
                  open={open}
                  uploadMode={uploadMode}
                />
              }
            >
              <SourceMediaPanelContent
                currentFile={currentFile}
                isDragAccept={isDragAccept}
                isDragReject={isDragReject}
                isEncoding={isEncoding}
                isLoading={isLoading}
                isQueued={isQueued}
                isSimpleMode={isSimpleMode}
                isUploadBlocked={isUploadBlocked}
                isUploadError={isUploadError}
                isUploading={isUploading}
                onResetUpload={onResetUpload}
                onSetSourceToDelete={setSourceToDelete}
                open={open}
                pendingSource={pendingSource}
                sortedSourceMediaList={sortedSourceMediaList}
                uploadedMimeTypeMap={uploadedMimeTypeMap}
                uploadMode={uploadMode}
                uploadProgress={uploadProgress}
              />
            </Block>
          </div>
        )}
      </Dropzone>

      <UploadFromUrlModal
        isOpen={isFromUrlModalOpen}
        onClose={(): void => setIsFromUrlModalOpen(false)}
        onSubmit={onUploadFromUrl}
      />

      <ConfirmModal
        actions={deleteModalActions}
        iconPath={mdiTrashCanOutline}
        isOpen={Boolean(sourceToDelete)}
        onClose={(): void => setSourceToDelete(null)}
        size="md"
        title={t("removeSourceModal.title")}
      >
        {t("removeSourceModal.body", { filename: sourceToDelete?.filename })}
      </ConfirmModal>
    </React.Fragment>
  );
}
